import { useState, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { Plus, Trash2 } from 'lucide-react';
import type { yaraExclusions } from '@kirin/api-client/contracts';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from './ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from './ui/alert-dialog';
import { Tooltip, TooltipContent, TooltipTrigger } from './ui/tooltip';
import { useToast } from '../hooks/use-toast';
import { getErrorMessage } from '../hooks/api/errorHelpers';
import { formatZonedDate } from '@/lib/date-utils';
import { useDisplayTimeZone } from '@/hooks/useDisplayTimeZone';

interface YaraExclusionsControlProps {
  exclusions: yaraExclusions.YaraExclusion[] | undefined;
  isLoading: boolean;
  onAdd: (
    data: yaraExclusions.AddYaraExclusionBody
  ) => Promise<yaraExclusions.AddYaraExclusionResponse>;
  onRemove: (extensionId: string) => Promise<void>;
  canModify?: boolean;
}

type AddExclusionFormData = yaraExclusions.AddYaraExclusionBody;

export function YaraExclusionsControl({
  exclusions = [],
  isLoading,
  onAdd,
  onRemove,
  canModify = true,
}: YaraExclusionsControlProps) {
  const timeZone = useDisplayTimeZone();
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const { toast } = useToast();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<AddExclusionFormData>({
    defaultValues: {
      extensionId: '',
      reason: '',
    },
  });

  const handleAdd = async (data: AddExclusionFormData) => {
    try {
      setIsProcessing(true);
      await onAdd(data);
      toast({
        title: 'Extension excluded',
        description: `${data.extensionId} has been added to the exclusion list`,
      });
      // Close dialog first, then reset form
      setIsAddDialogOpen(false);
      reset();
    } catch (error) {
      toast({
        title: 'Error adding exclusion',
        description: getErrorMessage(error),
        variant: 'destructive',
      });
      // Keep dialog open on error so user can retry
    } finally {
      setIsProcessing(false);
    }
  };

  const handleRemove = async (extensionId: string) => {
    try {
      setIsProcessing(true);
      await onRemove(extensionId);
      toast({
        title: 'Exclusion removed',
        description: `${extensionId} has been removed from the exclusion list`,
      });
    } catch (error) {
      toast({
        title: 'Error removing exclusion',
        description: getErrorMessage(error),
        variant: 'destructive',
      });
    } finally {
      setIsProcessing(false);
    }
  };

  // Sort exclusions by extension ID
  const sortedExclusions = useMemo(() => {
    if (!exclusions) return [];
    return [...exclusions].sort((a, b) =>
      a.extensionId.localeCompare(b.extensionId)
    );
  }, [exclusions]);

  // Truncate reason text to a short sentence (50 characters)
  const truncateReason = (
    reason: string | null,
    maxLength: number = 50
  ): string => {
    if (!reason) return '';
    if (reason.length <= maxLength) return reason;
    return reason.substring(0, maxLength) + '...';
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Code Scanning Excluded Extensions</CardTitle>
            <CardDescription>
              Define extensions that should be excluded from Extensions Code
              Scan.
            </CardDescription>
          </div>
          {canModify && (
            <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
              <DialogTrigger asChild>
                <Button size="sm">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Exclusion
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Add Extension to Exclusion List</DialogTitle>
                  <DialogDescription>
                    Exclude an extension from Extensions Code Scan. This is
                    useful for false positives that have been verified as safe.
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleSubmit(handleAdd)}>
                  <div className="space-y-4 py-4">
                    <div className="space-y-2">
                      <Label htmlFor="extensionId">
                        Extension ID <span className="text-destructive">*</span>
                      </Label>
                      <Input
                        id="extensionId"
                        placeholder="publisher.extensionName"
                        {...register('extensionId', {
                          required: 'Extension ID is required',
                          pattern: {
                            value: /^[^.]+\.\w+/,
                            message:
                              'Invalid format. Expected: publisher.extensionName',
                          },
                        })}
                        disabled={isProcessing}
                      />
                      {errors.extensionId && (
                        <p className="text-sm text-destructive">
                          {errors.extensionId.message}
                        </p>
                      )}
                      <p className="text-sm text-muted-foreground">
                        Format: publisher.extensionName (e.g., microsoft.python)
                      </p>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="reason">Reason (optional)</Label>
                      <Input
                        id="reason"
                        placeholder="False positive - verified safe"
                        {...register('reason')}
                        disabled={isProcessing}
                      />
                      <p className="text-sm text-muted-foreground">
                        Optional reason for excluding this extension
                      </p>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        reset();
                        setIsAddDialogOpen(false);
                      }}
                      disabled={isProcessing}
                    >
                      Cancel
                    </Button>
                    <Button type="submit" disabled={isProcessing}>
                      {isProcessing ? 'Adding...' : 'Add Exclusion'}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="text-sm text-muted-foreground">
            Loading exclusions...
          </div>
        ) : exclusions.length === 0 ? (
          <div className="text-sm text-muted-foreground">
            <p>No excluded extensions configured.</p>
            <p className="mt-2">
              Add extensions to exclude from Extensions Code Scan when they are
              false positives.
            </p>
          </div>
        ) : (
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Extension ID</TableHead>
                  <TableHead>Reason</TableHead>
                  <TableHead>Excluded By</TableHead>
                  <TableHead>Date Added</TableHead>
                  {canModify && (
                    <TableHead className="w-[100px]">Actions</TableHead>
                  )}
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedExclusions.map(exclusion => (
                  <TableRow key={exclusion.id}>
                    <TableCell className="font-mono text-sm">
                      {exclusion.extensionId}
                    </TableCell>
                    <TableCell>
                      {exclusion.reason ? (
                        exclusion.reason.length > 100 ? (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="cursor-help">
                                {truncateReason(exclusion.reason)}
                              </span>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p className="max-w-xs">{exclusion.reason}</p>
                            </TooltipContent>
                          </Tooltip>
                        ) : (
                          exclusion.reason
                        )
                      ) : (
                        <span className="text-muted-foreground italic">
                          No reason provided
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      {exclusion.excludedBy || (
                        <span className="text-muted-foreground">Unknown</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {formatZonedDate(exclusion.createdAt, timeZone)}
                    </TableCell>
                    {canModify && (
                      <TableCell>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              disabled={isProcessing}
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>
                                Remove Exclusion?
                              </AlertDialogTitle>
                              <AlertDialogDescription>
                                Are you sure you want to remove{' '}
                                <span className="font-mono font-semibold">
                                  {exclusion.extensionId}
                                </span>{' '}
                                from the exclusion list? YARA rule alerts will
                                be shown for this extension again.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() =>
                                  handleRemove(exclusion.extensionId)
                                }
                                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                              >
                                Remove
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
